import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useGameStore } from '../stores/gameStore'
import { useSocket } from '../context/SocketContext'

const ChatPage = () => {
  const { chatId } = useParams()
  const navigate = useNavigate()
  const [message, setMessage] = useState('')
  const [messages, setMessages] = useState([])
  const [partnerIdentity, setPartnerIdentity] = useState('🎭🌙⭐')
  const [isActive, setIsActive] = useState(true)
  const [partnerLeft, setPartnerLeft] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const messagesEndRef = useRef(null)
  
  const { player, setGameState, resetGame, resetGameKeepIdentity, matchRequestData, lastMatchResult } = useGameStore()
  const { sendChatMessage, joinLobby, socket } = useSocket()

  // Scroll to top when entering chat
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  // Set partner identity from game data
  useEffect(() => {
    if (matchRequestData && matchRequestData.partner_identity) {
      setPartnerIdentity(matchRequestData.partner_identity)
    } else if (lastMatchResult && lastMatchResult.partner_identity) {
      setPartnerIdentity(lastMatchResult.partner_identity)
    }
  }, [matchRequestData, lastMatchResult])

  useEffect(() => {
    if (!player.identity) {
      navigate('/')
      return
    }

    // Set up chat event listeners
    if (socket) {
      socket.on('chat_message', (data) => {
        setMessages(prev => [...prev, data])
      })

      socket.on('chat_ended', () => {
        setIsActive(false)
      })

      socket.on('partner_left', () => {
        setIsActive(false)
        setMessages(prev => [...prev, {
          type: 'system',
          message: 'Your partner has left the chat.',
          timestamp: Date.now()
        }])
      })

      socket.on('partner_left_chat', (data) => {
        setIsActive(false)
        setPartnerLeft(true)
        setMessages(prev => [...prev, {
          type: 'system',
          message: data.message,
          timestamp: Date.now()
        }])
      })

      // Join the chat room
      socket.emit('join_chat', { chat_id: chatId })
    }

    return () => {
      if (socket) {
        socket.off('chat_message')
        socket.off('chat_ended')
        socket.off('partner_left')
        socket.off('partner_left_chat')
      }
    }
  }, [socket, chatId, player.identity, navigate])

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSendMessage = (e) => {
    e.preventDefault()
    if (!message.trim() || !isActive) return

    // Send message to server - server will echo it back to display
    sendChatMessage(chatId, message.trim())
    setMessage('')
  }

  const handleLeaveChat = () => {
    if (socket) {
      socket.emit('leave_chat', { chat_id: chatId })
    }
    
    // Reset game state but keep identity, and go to lobby
    resetGameKeepIdentity()
    
    // LobbyPage will handle joining when it mounts
    
    navigate('/lobby')
  }

  const handleReturnToPurgatory = () => {
    // Reset game state but keep identity, and go to lobby
    resetGameKeepIdentity()
    
    // LobbyPage will handle joining when it mounts
    
    navigate('/lobby')
  }

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    })
  }

  // Calculate common ground percentage
  const getCommonGroundPercentage = () => {
    if (lastMatchResult && lastMatchResult.game_summary) {
      return lastMatchResult.game_summary.common_ground_percentage || 0
    }
    return 0
  }

  // Debug: Log the full data structure
  console.log('ChatPage - Full lastMatchResult:', lastMatchResult)
  console.log('ChatPage - game_summary:', lastMatchResult?.game_summary)
  console.log('ChatPage - questions array:', lastMatchResult?.questions)
  console.log('ChatPage - player1_answers:', lastMatchResult?.player1_answers)
  console.log('ChatPage - player2_answers:', lastMatchResult?.player2_answers)

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-4xl mx-auto">
        {/* Chat Header */}
        <div className="flex gap-4 mb-6">
          {/* You Card */}
          <div className="card-retro p-6 flex-1 text-center">
            <div className="text-4xl mb-2">{player.identity}</div>
            <div className="text-purgatory-mist text-sm">You</div>
          </div>
          
          {/* Your Partner Card */}
          <div className="card-retro p-6 flex-1 text-center">
            <div className="text-4xl mb-2">{partnerIdentity}</div>
            <div className="text-purgatory-mist text-sm">Your partner</div>
          </div>
        </div>

        {/* Results Section - Collapsible */}
        {lastMatchResult && lastMatchResult.game_summary && (
          <div className="card-retro p-6 mb-6">
            <button
              onClick={() => setShowResults(!showResults)}
              className="w-full text-left flex items-center justify-between"
            >
              <h3 className="text-lg font-bold text-neon-blue">
                📊 Your Results
              </h3>
              <span className="text-purgatory-mist">
                {showResults ? '▼' : '▶'}
              </span>
            </button>
            
            {showResults && (
              <div className="mt-4 space-y-4">
                {/* Overall Summary */}
                <div className="text-center p-4 bg-purgatory-dark rounded border">
                  <div className="text-2xl font-bold text-neon-blue mb-2">
                    {getCommonGroundPercentage()}% Common Ground
                  </div>
                  <div className="text-sm text-purgatory-mist">
                    {lastMatchResult.game_summary.total_matches} matches out of {lastMatchResult.game_summary.total_questions} questions
                  </div>
                </div>

                {/* Round Breakdown */}
                {lastMatchResult.game_summary && lastMatchResult.game_summary.rounds && lastMatchResult.game_summary.rounds.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-md font-bold text-purgatory-gold">Round Breakdown:</h4>
                    {lastMatchResult.game_summary.rounds.map((round, roundIndex) => (
                      <div key={roundIndex} className="p-3 bg-purgatory-dark rounded border">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-bold text-purgatory-mist">Round {roundIndex + 1}</span>
                          <span className="text-neon-green">
                            {Math.round(((round.matches_count || round.matches || 0) / (round.questions ? round.questions.length : 3)) * 100)}%
                          </span>
                        </div>
                        {round.questions && round.questions.length > 0 && (
                          <div className="space-y-3 text-sm">
                            {round.questions.map((question, qIndex) => {
                              // Calculate the global question index across all rounds
                              let globalQIndex = qIndex;
                              for (let i = 0; i < roundIndex; i++) {
                                if (lastMatchResult.game_summary.rounds[i] && lastMatchResult.game_summary.rounds[i].questions) {
                                  globalQIndex += lastMatchResult.game_summary.rounds[i].questions.length;
                                }
                              }
                              
                              // Debug logging
                              console.log(`Round ${roundIndex + 1}, Question ${qIndex + 1} (Global ${globalQIndex + 1}):`, {
                                roundIndex,
                                qIndex,
                                globalQIndex,
                                roundUser1Answers: round.user1_answers,
                                roundUser2Answers: round.user2_answers,
                                questionOptions: question.options,
                                player1AnswerIndex: round.user1_answers?.[qIndex],
                                player2AnswerIndex: round.user2_answers?.[qIndex]
                              });
                              
                              const player1Answer = round.user1_answers && question.options && question.options[round.user1_answers[qIndex]];
                              const player2Answer = round.user2_answers && question.options && question.options[round.user2_answers[qIndex]];
                              const isMatch = round.match_details && round.match_details[qIndex];
                              
                              return (
                                <div key={qIndex} className="bg-purgatory-ash bg-opacity-20 p-3 rounded">
                                  <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm text-purgatory-mist">
                                      Question {globalQIndex + 1}
                                    </span>
                                    <span className="text-xs">
                                      {isMatch ? (
                                        <span className="text-neon-green">✓ MATCH</span>
                                      ) : (
                                        <span className="text-purgatory-ember">✗ NO MATCH</span>
                                      )}
                                    </span>
                                  </div>
                                  <div className="text-sm mb-2">{question.text}</div>
                                  <div className="grid grid-cols-2 gap-4 text-xs">
                                    <div>
                                      <span className="text-purgatory-mist">You: </span>
                                      <span className={`font-bold ${
                                        isMatch ? 'text-neon-green' : 'text-purgatory-ember'
                                      }`}>
                                        {player1Answer || 'No answer'}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-purgatory-mist">Them: </span>
                                      <span className={`font-bold ${
                                        isMatch ? 'text-neon-green' : 'text-purgatory-ember'
                                      }`}>
                                        {player2Answer || 'No answer'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Chat container */}
        <div className="card-retro h-96 flex flex-col">

          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto hide-scrollbar space-y-3">
            {messages.length === 0 ? (
              <div className="text-center text-purgatory-mist py-8">
                <div className="text-4xl mb-4">💬</div>
                <p className="text-lg mb-2">You had {getCommonGroundPercentage()}% in common.</p>
                <p className="text-sm mb-2">Here's your opportunity to discuss.</p>
                <p className="text-xs text-purgatory-ember">Keep it friendly y'all!</p>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div key={index} className={`flex ${
                  msg.sender === 'you' ? 'justify-end' : 'justify-start'
                }`}>
                  {msg.type === 'system' ? (
                    <div className="text-center text-purgatory-mist text-xs italic py-2">
                      {msg.message}
                    </div>
                  ) : (
                    <div className={`chat-bubble ${
                      msg.sender === 'you' ? 'own' : 'other'
                    }`}>
                      <div className="text-sm">{msg.message}</div>
                      <div className="text-xs opacity-60 mt-1">
                        {formatTime(msg.timestamp)}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-purgatory-ash p-4">
            {isActive ? (
              <form onSubmit={handleSendMessage} className="flex space-x-3">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Share your thoughts on your escape..."
                  className="input-retro flex-1"
                  maxLength={200}
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={!message.trim()}
                  className="btn-retro px-6"
                >
                  Send
                </button>
              </form>
            ) : (
              <div className="text-center text-purgatory-mist">
                Chat has ended. The moment has passed.
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center mt-6">
          <div className="text-xs text-purgatory-mist">
            {/* Removed the reward text as requested */}
          </div>
          <div className="flex space-x-3">
            {partnerLeft && (
              <button
                onClick={handleReturnToPurgatory}
                className="btn-retro btn-secondary"
              >
                🏠 Return to Purgatory
              </button>
            )}
            <button
              onClick={handleLeaveChat}
              className="btn-retro btn-danger"
            >
              Leave Chat
            </button>
          </div>
        </div>

      </div>
    </div>
  )
}

export default ChatPage
